import { useEffect, useRef, useState } from "react";
import { REIProgressSpinner } from "../../../components/ui";
import { useAnalysedPropertyContext } from "./Child/AnalysedPropertyContext";
import { httpPost, httpUpdate } from "../../../services/httpService";
import { ApiEndPoints } from "../../../utils/endpoints";
import { useNavigate } from "react-router-dom";
import { Toast } from "primereact/toast";
import { Messages } from "../../../utils/constants";
import { IMemberEntity } from "../../../interface/analysed";

interface IResults {
  memberEntities?: IMemberEntity[];
}

const Results: React.FC<IResults> = ({ memberEntities }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const { analysedProperty, setAnalysedProperty } = useAnalysedPropertyContext();
  const navigation = useNavigate();
  const toastRef = useRef<Toast>(null);
  const hasUpdated = useRef(false); // Track if updatePropertyDetails has already been called

  useEffect(() => {
    if (!hasUpdated.current) {
      updatePropertyDetails();
      hasUpdated.current = true; // Mark as updated to prevent re-triggering
    }
  }, [analysedProperty]);

  const updatePropertyDetails = async () => {
    if (analysedProperty && analysedProperty.PropertyDetail && analysedProperty.PropertyDetail.useDefault === null) {
      setAnalysedProperty((prev: any) => ({
        ...prev,
        PropertyDetail: {
          ...prev.PropertyDetail,
          useDefault: "0",
        },
      }));
      analysedProperty.PropertyDetail.useDefault = "0";
    }
    if (analysedProperty?.PropertyDetail?.PropertyDetailId && analysedProperty?.PropertyDetail?.PropertyDetailId > 0) {
      analysedProperty.PropertyDetail.CarportId =
        analysedProperty.PropertyDetail.CarportId === 0 ? 1 : analysedProperty.PropertyDetail.CarportId;
      analysedProperty.LoanTypeCost.Loans = analysedProperty.LoanTypeCost.Loans.map((loan) => ({
        ...loan,
        TrackerLoanId: 0,
        LoanId: undefined,
        PropertyAnalyserid: 0,
        OtherLoanCost: loan.OtherLoanCosts,
        Length_Years: loan.Length_Years === 0 ? 31 : loan.Length_Years,
        Length_Months: loan.Length_Months === 0 ? 1 : loan.Length_Months,
      }));

      if (
        analysedProperty?.PropertyDetail?.MemberEntityId === null ||
        analysedProperty?.PropertyDetail?.MemberEntityId === undefined ||
        analysedProperty?.PropertyDetail?.MemberEntityId === "0"
      ) {
        console.log("Setting default MemberEntityId");
        analysedProperty.PropertyDetail.MemberEntityId =
          memberEntities && memberEntities.length > 0 ? memberEntities[0].MemberEntityId.toString() : "0";
      }

      await httpUpdate(`${ApiEndPoints.analyser}/PropertyAnalyserDetail`, analysedProperty)
        .then((result) => {
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: Messages.SUCCESS,
            life: 2000,
          });
          setTimeout(() => {
            navigation("/property-analyser/analysis-report", {
              state: {
                propertyAnalyserId: analysedProperty?.PropertyDetail.PropertyDetailId,
                propertyName: [
                  `${analysedProperty?.PropertyDetail.PropertyName}`,
                  `${analysedProperty?.PropertyDetail.locality}`,
                  `${analysedProperty?.PropertyDetail.district}`,
                  `${analysedProperty?.PropertyDetail.state}`,
                  `${analysedProperty?.PropertyDetail.postcode}`,
                ]
                  .filter(Boolean)
                  .join(", "),
                countryID: analysedProperty?.PropertyDetail.CountryId,
              },
            });
          }, 2000);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          toastRef.current?.show({
            severity: "error",
            summary: Messages.ERROR,
            detail: Messages.ERROR,
            life: 2000,
          });
          setTimeout(() => {
            navigation("/property-analyser");
          }, 1000);
        });
    } else {
      if (analysedProperty?.PropertyDetail?.PropertyDetailId === 0) {
        analysedProperty.LoanTypeCost.Loans = analysedProperty.LoanTypeCost.Loans.map((loan) => ({
          ...loan,
          TrackerLoanId: 0,
          LoanId: undefined,
          PropertyAnalyserid: 0,
          Length_Years: loan.Length_Years === 0 ? 31 : loan.Length_Years,
          Length_Months: loan.Length_Months === 0 ? 1 : loan.Length_Months,
        }));
      }
      await httpPost(`${ApiEndPoints.analyser}/PropertyAnalyserDetail`, analysedProperty)
        .then((result) => {
          if (result.status !== 200) {
            toastRef.current?.show({
              severity: "error",
              summary: Messages.ERROR,
              detail: "Error in creating property analyser",
              life: 2000,
            });
            setTimeout(() => {
              navigation("/property-analyser");
            }, 1000);
          }
          toastRef.current?.show({
            severity: "success",
            summary: Messages.SUCCESS,
            detail: Messages.SUCCESS,
            life: 2000,
          });
          setTimeout(() => {
            navigation("/property-analyser/analysis-report", {
              state: {
                propertyAnalyserId: result.data,
                propertyName: `${analysedProperty?.PropertyDetail.PropertyName || `${analysedProperty?.PropertyDetail.locality}, ${analysedProperty?.PropertyDetail.district}, ${analysedProperty?.PropertyDetail.state},${analysedProperty?.PropertyDetail.postcode}`}`,
                countryID: analysedProperty?.PropertyDetail.CountryId,
              },
            });
          }, 2000);
          setLoading(false);
        })
        .catch(() => {
          setLoading(false);
          toastRef.current?.show({
            severity: "error",
            summary: Messages.ERROR,
            detail: Messages.ERROR,
            life: 2000,
          });
          setTimeout(() => {
            navigation("/property-analyser");
          }, 1000);
        });
    }
  };

  return (
    <>
      {loading && (
        <div className="flex items-center">
          <REIProgressSpinner></REIProgressSpinner>
        </div>
      )}
      <Toast ref={toastRef} />
    </>
  );
};

export default Results;
